export enum InsuranceEligibility {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum PlanStatusCode {
  ACTIVE = '1',
  INACTIVE = '6',
}

export enum ServiceTypeCode {
  SPEECH_THERAPY = 'AF',
  GENERAL_HEALTH = '30',
}

export interface EligibilityCheckResponse {
  insuranceEligibility: InsuranceEligibility;
  stediDetailsURL?: string;
  eligibilityDetails: {
    planStatus: {
      speechTherapy: Array<{
        statusCode: string;
        status: string;
        serviceTypeCodes: string[];
      }>;
      generalHealth: Array<{
        statusCode: string;
        status: string;
        planDetails: string;
        serviceTypeCodes: string[];
      }>;
    };
    planDateInformation: {
      planBegin?: string;
      plan?: string;
      certification: string;
    };
    benefitsInformation: {
      speechTherapy?: Array<{
        code: string;
        name: string;
        coverageLevelCode?: string;
        coverageLevel?: string;
        serviceTypeCodes: string[];
        serviceTypes: string[];
        inPlanNetworkIndicatorCode: string;
        inPlanNetworkIndicator: string;
        timeQualifierCode?: string;
        timeQualifier?: string;
        benefitAmount?: string;
        authOrCertIndicator?: string;
        benefitsRelatedEntity?: {
          entityIdentifier: string;
          entityType: string;
          entityName: string;
          contactInformation: {
            name: string;
            contacts: Array<{
              communicationMode: string;
              communicationNumber: string;
            }>;
          };
        };
        benefitsRelatedEntities?: Array<{
          entityIdentifier: string;
          entityType: string;
          entityName: string;
          contactInformation: {
            name: string;
            contacts: Array<{
              communicationMode: string;
              communicationNumber: string;
            }>;
          };
        }>;
        additionalInformation?: Array<{
          description: string;
        }>;
      }>;
      generalHealth?: Array<{
        code: string;
        name: string;
        serviceTypeCodes: string[];
        serviceTypes: string[];
        insuranceTypeCode: string;
        insuranceType: string;
        planCoverage: string;
        inPlanNetworkIndicatorCode: string;
        inPlanNetworkIndicator: string;
      }>;
      primaryCareProvider?: {
        code: string;
        name: string;
        benefitsDateInformation: {
          primaryCareProvider: string;
        };
        benefitsRelatedEntity: {
          entityIdentifier: string;
          entityType: string;
          entityName: string;
          entityFirstname?: string;
          entityMiddlename?: string;
          entityIdentification?: string;
          entityIdentificationValue?: string;
          address?: {
            address1: string;
            city: string;
            state: string;
            postalCode: string;
          };
          contactInformation?: {
            contacts: Array<{
              communicationMode: string;
              communicationNumber: string;
            }>;
          };
        };
        benefitsRelatedEntities?: Array<{
          entityIdentifier: string;
          entityType: string;
          entityName: string;
          entityFirstname?: string;
          entityMiddlename?: string;
          entityIdentification?: string;
          entityIdentificationValue?: string;
          address?: {
            address1: string;
            city: string;
            state: string;
            postalCode: string;
          };
          contactInformation?: {
            contacts: Array<{
              communicationMode: string;
              communicationNumber: string;
            }>;
          };
        }>;
      } | null;
    };
  };
  estimates?: Record<string, string>;
  speechTherapy?: Record<string, string>;
  errorMessages?: string;
}

// New interface for eligibility check result that includes both status and PCP information
export interface EligibilityCheckResultWithPCP {
  status: EligibilityCheckResult;
  pcp?: {
    name: string;
    firstName?: string;
    middleName?: string;
    phone?: string;
    fax?: string;
    npi?: string;
    address?: {
      address1: string;
      city: string;
      state: string;
      postalCode: string;
    };
  } | null;
}

export enum LeadEligibleResponse {
  ELIGIBLE = 'inn',
  ELIGIBLE_INACTIVE = 'inactive',
  NOT_ELIGIBLE = 'onn',
  ERROR = 'error',
}

export enum StediInplanBenefitNetworkIndicator {
  IN_NETWORK = 'Y',
  OUT_OF_NETWORK = 'N',
  UNKNOWN = 'U',
}

export enum ServiceTypeCodes {
  SPEECH_THERAPY = 'AF',
  GENERAL_HEALTH = '30',
}

export enum EligibilityCheckResult {
  IN_NETWORK = 'inn',
  INACTIVE = 'inactive',
  ERROR = 'error',
  OUT_OF_NETWORK = 'onn',
  INCORRECT_MEMBER_ID = 'incorrectmemberid',
  INCORRECT_NAME = 'incorrectname',
  INCORRECT_DOB = 'incorrectdob',
  INVALID_PARTICIPANT_ID = 'invalidparticipantid',
}

export enum StediEligibilityBenefitCode {
  ACTIVE_COVERAGE = '1',
  ACTIVE_FULL_RISK_CAPITATION = '2',
  ACTIVE_SERVICES_CAPITATED = '3',
  ACTIVE_SERVICES_CAPITATED_TO_PCP = '4',
  ACTIVE_PENDING_INVESTIGATION = '5',
  INACTIVE = '6',
  INACTIVE_PENDING_ELIGIBILITY_UPDATE = '7',
  ACTIVE_PENDING_INVESTIGATION_ALT = '8',
  CO_INSURANCE = 'A',
  CO_PAYMENT = 'B',
  DEDUCTIBLE = 'C',
  COVERAGE_BASIS = 'CB',
  BENEFIT_DESCRIPTION = 'D',
  EXCLUSIONS = 'E',
  LIMITATIONS = 'F',
  OUT_OF_POCKET = 'G',
  UNLIMITED = 'H',
  NON_COVERED = 'I',
  COST_CONTAINMENT = 'J',
  RESERVE = 'K',
  PRIMARY_CARE_PROVIDER = 'L',
  PRE_EXISTING_CONDITION = 'M',
  MANAGED_CARE_COORDINATOR = 'MC',
  SERVICES_RESTRICTED_TO_PROVIDER = 'N',
  NOT_MEDICAL_NECESSITY = 'O',
  BENEFIT_DISCLAIMER = 'P',
  SECOND_SURGICAL_OPINION = 'Q',
  OTHER_PAYOR = 'R',
  PRIOR_YEARS_HISTORY = 'S',
  CARDS_REPORTED_LOST_STOLEN = 'T',
  CONTACT_ENTITY_FOR_INFO = 'U',
  CANNOT_PROCESS = 'V',
  OTHER_SOURCE_OF_DATA = 'W',
  HEALTH_CARE_FACILITY = 'X',
  SPEND_DOWN = 'Y',
}
